// Partie payante : les scènes « À toi » (`interactive`). Sans accès à la
// SKILLCARD source, le serveur sert le document SANS elles : scènes
// interactives retirées, objets, ressources et paramètres qu'elles seules
// utilisent retirés aussi (une réponse affichée dans la partie payante ne
// doit pas voyager dans le JSON gratuit — même un objet « visible » au départ
// mais transparent, révélé ensuite par la partie payante). Le résultat reste
// un document valide.
//
// La validation garantit les préconditions : au moins une scène gratuite,
// les scènes interactives APRÈS les scènes gratuites (l'état des objets à la
// fin de la partie gratuite ne dépend donc pas d'elles), `cardId` présent.

import type { HoraKey, JrafDocument } from './schema/document';
import type { JrafObject } from './schema/objects';
import { stepTargets } from './schema/steps';
import { JRAF_LIMITS as L } from './limits';

export interface LockedScene {
  id: string;
  title: string;
  hora?: HoraKey;
}

export interface FreeAnimation {
  /** document sans la partie interactive (valide) */
  doc: JrafDocument;
  /** scènes retirées, pour afficher « La suite dans la SKILLCARD » */
  locked: LockedScene[];
}

export function hasInteractivePart(doc: Pick<JrafDocument, 'scenes'>): boolean {
  return doc.scenes.some((s) => s.interactive);
}

/** Retire la partie interactive d'un document VALIDÉ (`validateJraf`). */
export function stripInteractive(doc: JrafDocument): FreeAnimation {
  const free = doc.scenes.filter((s) => !s.interactive);
  const locked: LockedScene[] = doc.scenes
    .filter((s) => s.interactive)
    .map((s) => ({ id: s.id, title: s.title, ...(s.hora ? { hora: s.hora } : {}) }));
  if (locked.length === 0) return { doc, locked };
  if (free.length === 0) throw new Error('stripInteractive : aucune scène gratuite');

  // objets gardés : visibles au départ, ou visés par une scène gratuite, et leurs groupes
  const byId = new Map<string, JrafObject>(doc.objects.map((o) => [o.id, o]));
  const keep = new Set<string>();
  for (const o of doc.objects) if (shownAtStart(o)) keep.add(o.id);
  for (const s of free) for (const st of s.steps) for (const t of stepTargets(st)) keep.add(t);
  for (const id of [...keep]) {
    let cur = byId.get(id);
    let guard = 0;
    while (cur?.parent !== undefined && guard++ <= L.maxGroupDepth) {
      keep.add(cur.parent);
      cur = byId.get(cur.parent);
    }
  }
  const objects = doc.objects.filter((o) => keep.has(o.id));

  const usedAssets = new Set<string>();
  for (const o of objects) if (o.type === 'image') usedAssets.add(o.asset);
  for (const s of free) if (s.narration.audioAssetId) usedAssets.add(s.narration.audioAssetId);
  if (doc.bookends?.hookAudioAssetId) usedAssets.add(doc.bookends.hookAudioAssetId);
  if (doc.bookends?.introAudioAssetId) usedAssets.add(doc.bookends.introAudioAssetId);
  if (doc.bookends?.outroAudioAssetId) usedAssets.add(doc.bookends.outroAudioAssetId);
  const assets = doc.assets.filter((a) => usedAssets.has(a.id));

  return {
    doc: { ...doc, parameters: usedParameters(doc, objects, free), objects, assets, scenes: free },
    locked,
  };
}

/** Vu au départ : ni caché, ni transparent (une opacité nulle se révèle plus tard). */
function shownAtStart(o: JrafObject): boolean {
  if (o.initial?.visible === false) return false;
  if (o.initial?.opacity === 0) return false;
  const style = 'style' in o ? (o.style as { opacity?: number } | undefined) : undefined;
  return style?.opacity !== 0;
}

/**
 * Paramètres utilisés par ce qui reste (et ceux dont ils dépendent) : une
 * constante qui ne sert qu'à la partie payante (une réponse calculée) part avec elle.
 */
function usedParameters(
  doc: JrafDocument,
  objects: readonly JrafObject[],
  scenes: readonly JrafDocument['scenes'][number][],
): JrafDocument['parameters'] {
  const words = (v: unknown) => new Set(JSON.stringify(v).match(/[A-Za-z_][A-Za-z0-9_]*/g) ?? []);
  const used = words([objects, scenes]);
  const byId = new Map(doc.parameters.map((p) => [p.id, p]));
  const queue = [...used].filter((w) => byId.has(w));
  const keep = new Set<string>(queue);
  while (queue.length) {
    const p = byId.get(queue.pop()!);
    if (p?.type !== 'constant') continue;
    for (const w of words(p.value))
      if (byId.has(w) && !keep.has(w)) {
        keep.add(w);
        queue.push(w);
      }
  }
  return doc.parameters.filter((p) => keep.has(p.id));
}
